/**
 * Блок сводки сверки (карточка BL-2026-09-29-beba).
 *
 * Flow: опрос /api/treasury/recon-breaks раз в 60 секунд → расхождения
 *       группируются по виду и платёжной системе → сколько платежей ждёт
 *       разбора и каких именно.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadReconBreaks.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает блок в разделе PSP/Bank;
 *   - src/ReconBreaksPanel.jsx — те же расхождения строками, с разбором.
 *
 * # ПОЧЕМУ ГРУППИРОВКА ЗДЕСЬ, А НЕ НА СЕРВЕРЕ
 *
 * Считаются ШТУКИ, а не деньги. Правило «суммы считает сервер» защищает
 * от второго экземпляра правила сложения денег, которое молча разойдётся
 * с выгрузкой; к подсчёту строк оно не относится. При этом ручка отдаёт
 * открытые расхождения ЦЕЛИКОМ, без страниц, — их десятки, а не тысячи, —
 * так что сгруппированное здесь число равно числу показанных строк
 * по построению. Отдельная ручка ради count(*) по двум десяткам строк
 * добавила бы место, где сводка и перечень могут разойтись.
 */

import { useCallback, useEffect, useState } from 'react'

import { loadReconBreaks } from './api'

/** Период опроса — как у остальных блоков платёжного контура. */
const POLL_MS = 60000

/** Человеческие названия видов расхождений — те же, что в блоке разбора. */
const KIND_TITLES = {
  psp_not_in_dealing: 'нет зачисления на дилинге',
  dealing_not_in_psp: 'зачисление без платежа',
  amount_mismatch: 'суммы разошлись',
  ambiguous_match: 'несколько кандидатов',
}

/** Названия платёжных систем. */
const SOURCE_TITLES = {
  praxis: 'Praxis Cashier',
  b2binpay: 'B2BinPay',
  coinsbuy: 'Coinsbuy',
}

/**
 * Группирует расхождения по виду и платёжной системе.
 *
 * @param {Array} breaks — открытые расхождения
 * @returns {Array<{kind: string, source: string, count: number}>} строки
 *          сводки, крупные сверху
 *
 * Порядок по убыванию количества: разбирают с того, чего больше.
 */
export function groupBreaks(breaks) {
  const map = new Map()
  for (const b of breaks || []) {
    // Расхождение без названной системы — не повод его потерять:
    // оно попадает в строку с пустой системой и остаётся видимым.
    const key = `${b.kind}|${b.source_code || ''}`
    if (!map.has(key)) {
      map.set(key, { kind: b.kind, source: b.source_code || '', count: 0 })
    }
    map.get(key).count += 1
  }
  return [...map.values()].sort((a, b) => b.count - a.count)
}

export default function ReconSummaryPanel() {
  const [breaks, setBreaks] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    loadReconBreaks(true)
      .then((d) => {
        setBreaks(d.breaks || [])
        setError('')
      })
      .catch((e) => setError(e.message))
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  if (error) {
    return (
      <section className="card">
        <h2>Сверка</h2>
        <div className="error">{error}</div>
      </section>
    )
  }

  if (breaks === null) {
    return (
      <section className="card">
        <h2>Сверка</h2>
        <p className="muted">Загрузка…</p>
      </section>
    )
  }

  const rows = groupBreaks(breaks)

  return (
    <section className="card">
      <h2>Сверка</h2>

      <p className="metric-big">
        {breaks.length} <span className="unit">на разбор</span>
      </p>
      <p className="muted metric-note">
        Платежи, по которым стороны не сошлись и причина ещё не записана.
      </p>

      {rows.length === 0 ? (
        // Ноль расхождений — состояние, о котором стоит сказать словами:
        // пустая таблица под нулём выглядит незагруженной.
        <p className="muted">Открытых расхождений нет — обе стороны сошлись.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Что не сошлось</th>
              <th>Система</th>
              <th className="num">Платежей</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.kind}-${r.source}`}>
                <td>{KIND_TITLES[r.kind] || r.kind}</td>
                <td>{r.source ? SOURCE_TITLES[r.source] || r.source : '—'}</td>
                <td className="num">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
